export enum ModerationCategory {
  ABUSE_HARASSMENT = 'ABUSE_HARASSMENT',
  SPAM_SCAM = 'SPAM_SCAM',
  PRODUCT_FEEDBACK = 'PRODUCT_FEEDBACK',
  FEATURE_REQUEST = 'FEATURE_REQUEST'
}

export enum ModerationSubReason {
  // ABUSE_HARASSMENT
  OFFENSIVE_NAME_IMAGE = 'OFFENSIVE_NAME_IMAGE',
  ABUSE_OF_PRIVACY = 'ABUSE_OF_PRIVACY',
  OTHER_ABUSE = 'OTHER_ABUSE',

  // SPAM_SCAM
  UNWANTED_ADS = 'UNWANTED_ADS',
  PHISHING_LINKS = 'PHISHING_LINKS',
  OTHER_SPAM = 'OTHER_SPAM',

  // PRODUCT_FEEDBACK
  CHEATING_EXPLOIT = 'CHEATING_EXPLOIT',
  WRONG_STATS = 'WRONG_STATS',
  DISPLAY_BUG = 'DISPLAY_BUG',
  OTHER_BUG_DATA = 'OTHER_BUG_DATA',

  // FEATURE_REQUEST
  FEATURE_REQUEST_IDEA = 'FEATURE_REQUEST_IDEA'
}

export interface ICategoryDefinition {
  category: ModerationCategory;
  icon: string;
  subReasons: ModerationSubReason[];
}

export const CATEGORY_DEFINITIONS: ICategoryDefinition[] = [
  {
    category: ModerationCategory.PRODUCT_FEEDBACK,
    icon: 'fa-wrench',
    subReasons: [
      ModerationSubReason.CHEATING_EXPLOIT,
      ModerationSubReason.WRONG_STATS,
      ModerationSubReason.DISPLAY_BUG,
      ModerationSubReason.OTHER_BUG_DATA
    ]
  },
  {
    category: ModerationCategory.ABUSE_HARASSMENT,
    icon: 'fa-ban',
    subReasons: [
      ModerationSubReason.OFFENSIVE_NAME_IMAGE,
      ModerationSubReason.ABUSE_OF_PRIVACY,
      ModerationSubReason.OTHER_ABUSE
    ]
  },
  {
    category: ModerationCategory.SPAM_SCAM,
    icon: 'fa-bullhorn',
    subReasons: [ModerationSubReason.UNWANTED_ADS, ModerationSubReason.PHISHING_LINKS, ModerationSubReason.OTHER_SPAM]
  },
  {
    category: ModerationCategory.FEATURE_REQUEST,
    icon: 'fa-lightbulb',
    subReasons: [ModerationSubReason.FEATURE_REQUEST_IDEA]
  }
];

export const ALL_CATEGORIES: ModerationCategory[] = Object.values(ModerationCategory);
export const ALL_SUB_REASONS: ModerationSubReason[] = Object.values(ModerationSubReason);

export const SUB_REASON_ICONS: Record<ModerationSubReason, string> = {
  [ModerationSubReason.CHEATING_EXPLOIT]: 'fa-skull-crossbones',
  [ModerationSubReason.WRONG_STATS]: 'fa-chart-line-down',
  [ModerationSubReason.DISPLAY_BUG]: 'fa-bug',
  [ModerationSubReason.OTHER_BUG_DATA]: 'fa-ellipsis',
  [ModerationSubReason.OFFENSIVE_NAME_IMAGE]: 'fa-comment-slash',
  [ModerationSubReason.ABUSE_OF_PRIVACY]: 'fa-user-lock',
  [ModerationSubReason.OTHER_ABUSE]: 'fa-ellipsis',
  [ModerationSubReason.UNWANTED_ADS]: 'fa-coins',
  [ModerationSubReason.PHISHING_LINKS]: 'fa-link-slash',
  [ModerationSubReason.OTHER_SPAM]: 'fa-ellipsis',
  [ModerationSubReason.FEATURE_REQUEST_IDEA]: 'fa-lightbulb'
};

export const CATEGORY_ACCENT_COLORS: Record<ModerationCategory, string> = {
  [ModerationCategory.PRODUCT_FEEDBACK]: '#5b9bd5',
  [ModerationCategory.ABUSE_HARASSMENT]: '#e05d5d',
  [ModerationCategory.SPAM_SCAM]: '#e0a030',
  [ModerationCategory.FEATURE_REQUEST]: '#5ec480'
};

export const DESCRIPTION_REQUIRED_SUB_REASONS = new Set<ModerationSubReason>(ALL_SUB_REASONS);

export function isDescriptionRequired(subReason: ModerationSubReason): boolean {
  return DESCRIPTION_REQUIRED_SUB_REASONS.has(subReason);
}

export function categoryLabelKey(category: ModerationCategory): string {
  return `moderation:categories.${category}.label`;
}

export function categoryDescriptionKey(category: ModerationCategory): string {
  return `moderation:categories.${category}.description`;
}

export function subReasonLabelKey(subReason: ModerationSubReason): string {
  return `moderation:sub-reasons.${subReason}.label`;
}

export function subReasonDescriptionKey(subReason: ModerationSubReason): string {
  return `moderation:sub-reasons.${subReason}.description`;
}

export function isValidCategory(value: string): value is ModerationCategory {
  return ALL_CATEGORIES.includes(value as ModerationCategory);
}

export function isValidSubReason(value: string): value is ModerationSubReason {
  return ALL_SUB_REASONS.includes(value as ModerationSubReason);
}

export function isSubReasonValidForCategory(category: ModerationCategory, subReason: ModerationSubReason): boolean {
  return CATEGORY_DEFINITIONS.find((d) => d.category === category)?.subReasons.includes(subReason) ?? false;
}
